import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { supabase } from "../lib/supabase";
import type { Dorama } from "../types";

type AdminDorama = Dorama & {
  original_title: string | null;
  hero_image_url: string | null;
  trailer_url: string | null;
  trailer_video_id: string | null;
  content_type: string;
  has_dubbing: boolean;
  has_subtitles: boolean;
  cast_members: string[];
  published: boolean;
  free_until: string | null;
};

type TrialAccess = {
  user_id: string;
  email: string;
  display_name: string | null;
  expires_at: string | null;
  notes: string | null;
};

type MediaRow = {
  dorama_id: string;
  bunny_video_id: string | null;
  full_video_id: string | null;
  full_video_id_sub: string | null;
  full_video_id_dub: string | null;
  video_folder_url: string | null;
};

type DoramaForm = {
  id: string;
  title: string;
  original_title: string;
  slug: string;
  description: string;
  category: string;
  content_type: string;
  price: string;
  cover_url: string;
  hero_image_url: string;
  trailer_url: string;
  trailer_video_id: string;
  bunny_video_id: string;
  full_video_id: string;
  full_video_id_sub: string;
  full_video_id_dub: string;
  video_folder_url: string;
  episodes: string;
  year: string;
  rating: string;
  cast_members: string;
  has_dubbing: boolean;
  has_subtitles: boolean;
  published: boolean;
  free_until: string;
};

const emptyForm: DoramaForm = {
  id: "", title: "", original_title: "", slug: "", description: "",
  category: "romance", content_type: "complete", price: "4.99", cover_url: "",
  hero_image_url: "", trailer_url: "", trailer_video_id: "", episodes: "0",
  bunny_video_id: "", full_video_id: "", full_video_id_sub: "", full_video_id_dub: "", video_folder_url: "",
  year: String(new Date().getFullYear()), rating: "9.0", cast_members: "",
  has_dubbing: false, has_subtitles: false, published: true, free_until: "",
};

const categories = [
  ["romance", "Romance"], ["action", "Ação"], ["drama", "Drama"],
  ["asian", "Asiático"], ["hot", "Hot"], ["turkish", "Turco"],
  ["brazilian", "Brasileiro"], ["comedy", "Comédia"],
  ["thriller", "Suspense"], ["fantasy", "Fantasia"],
];

function toForm(item: AdminDorama, media?: MediaRow): DoramaForm {
  return {
    id: item.id,
    title: item.title,
    original_title: item.original_title ?? "",
    slug: item.slug ?? "",
    description: item.description ?? "",
    category: item.category ?? "drama",
    content_type: item.content_type ?? "complete",
    price: String(item.price ?? 4.99),
    cover_url: item.cover_url ?? "",
    hero_image_url: item.hero_image_url ?? "",
    trailer_url: item.trailer_url ?? "",
    trailer_video_id: item.trailer_video_id ?? "",
    bunny_video_id: media?.bunny_video_id ?? "",
    full_video_id: media?.full_video_id ?? "",
    full_video_id_sub: media?.full_video_id_sub ?? "",
    full_video_id_dub: media?.full_video_id_dub ?? "",
    video_folder_url: media?.video_folder_url ?? "",
    episodes: String(item.episodes ?? 0),
    year: item.year ? String(item.year) : "",
    rating: String(item.rating ?? 9),
    cast_members: item.cast_members?.join(", ") ?? "",
    has_dubbing: item.has_dubbing,
    has_subtitles: item.has_subtitles,
    published: item.published,
    free_until: item.free_until ? item.free_until.slice(0, 16) : "",
  };
}

export function AdminPage() {
  const { user, loading, isAdmin, roleLoading } = useAuth();
  const [tab, setTab] = useState<"catalog" | "access">("catalog");
  const [doramas, setDoramas] = useState<AdminDorama[]>([]);
  const [mediaByDorama, setMediaByDorama] = useState<Record<string, MediaRow>>({});
  const [trials, setTrials] = useState<TrialAccess[]>([]);
  const [form, setForm] = useState<DoramaForm>(emptyForm);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [access, setAccess] = useState({ type: "dorama", email: "", doramaId: "", days: "7", notes: "" });

  const loadDoramas = useCallback(async () => {
    setCatalogLoading(true);
    const [catalogResult, mediaResult] = await Promise.all([
      supabase.from("doramas").select("*").order("created_at", { ascending: false }),
      supabase.from("dorama_media").select("dorama_id, bunny_video_id, full_video_id, full_video_id_sub, full_video_id_dub, video_folder_url"),
    ]);
    if (catalogResult.error) setMessage({ type: "error", text: `Não foi possível carregar o catálogo: ${catalogResult.error.message}` });
    else setDoramas((catalogResult.data ?? []) as AdminDorama[]);
    if (!mediaResult.error) setMediaByDorama(Object.fromEntries(((mediaResult.data ?? []) as MediaRow[]).map((item) => [item.dorama_id, item])));
    setCatalogLoading(false);
  }, []);

  const loadTrials = useCallback(async () => {
    const { data, error } = await supabase.rpc("admin_list_trial_accesses");
    if (error) setMessage({ type: "error", text: `Não foi possível carregar os acessos: ${error.message}` });
    else setTrials((data ?? []) as TrialAccess[]);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void loadDoramas();
    void loadTrials();
  }, [isAdmin, loadDoramas, loadTrials]);

  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("pt-BR");
    if (!term) return doramas;
    return doramas.filter((item) => `${item.title} ${item.category} ${item.slug}`.toLocaleLowerCase("pt-BR").includes(term));
  }, [doramas, query]);

  if (loading || roleLoading) return <div className="page-container admin-page"><div className="status-box">Verificando acesso administrativo…</div></div>;
  if (!user) return <div className="page-container admin-page"><div className="status-box"><h1>Entre para continuar</h1><p>O painel administrativo exige uma conta autorizada.</p><Link className="primary-button" to="/?entrar=1">Entrar</Link></div></div>;
  if (!isAdmin) return <div className="page-container admin-page"><div className="status-box error"><h1>Acesso restrito</h1><p>Esta conta não possui o papel de administrador.</p><Link className="secondary-button" to="/">Voltar ao início</Link></div></div>;

  async function saveDorama(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const payload = {
      title: form.title.trim(), original_title: form.original_title.trim() || null,
      slug: form.slug.trim(), description: form.description.trim(), category: form.category,
      content_type: form.content_type, price: Number(form.price), cover_url: form.cover_url.trim() || null,
      hero_image_url: form.hero_image_url.trim() || null, trailer_url: form.trailer_url.trim() || null,
      trailer_video_id: form.trailer_video_id.trim() || null, episodes: Number(form.episodes),
      year: form.year ? Number(form.year) : null, rating: Number(form.rating),
      cast_members: form.cast_members.split(",").map((name) => name.trim()).filter(Boolean),
      has_dubbing: form.has_dubbing, has_subtitles: form.has_subtitles, published: form.published,
      free_until: form.free_until ? new Date(form.free_until).toISOString() : null,
    };
    const result = form.id
      ? await supabase.from("doramas").update(payload).eq("id", form.id).select("id").single()
      : await supabase.from("doramas").insert(payload).select("id").single();
    if (result.error) setMessage({ type: "error", text: `Não foi possível salvar: ${result.error.message}` });
    else {
      const doramaId = result.data.id as string;
      const mediaPayload = {
        dorama_id: doramaId,
        bunny_video_id: form.bunny_video_id.trim() || null,
        full_video_id: form.full_video_id.trim() || null,
        full_video_id_sub: form.full_video_id_sub.trim() || null,
        full_video_id_dub: form.full_video_id_dub.trim() || null,
        video_folder_url: form.video_folder_url.trim() || null,
      };
      const hasMedia = Object.values(mediaPayload).some((value, index) => index > 0 && value);
      const mediaResult = hasMedia ? await supabase.from("dorama_media").upsert(mediaPayload) : { error: null };
      if (mediaResult.error) setMessage({ type: "error", text: `O título foi salvo, mas a mídia falhou: ${mediaResult.error.message}` });
      else {
        setMessage({ type: "success", text: form.id ? "Título e mídia atualizados." : "Título criado." });
        setForm(emptyForm);
        await loadDoramas();
      }
    }
    setBusy(false);
  }

  async function togglePublished(item: AdminDorama) {
    setMessage(null);
    const { error } = await supabase.from("doramas").update({ published: !item.published }).eq("id", item.id);
    if (error) setMessage({ type: "error", text: `Não foi possível alterar a publicação: ${error.message}` });
    else await loadDoramas();
  }

  async function grantAccess(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const result = access.type === "dorama"
      ? await supabase.rpc("admin_grant_dorama_access", { _email: access.email.trim(), _dorama_id: access.doramaId, _notes: access.notes.trim() || null })
      : await supabase.rpc("admin_grant_trial_access", { _email: access.email.trim(), _days: Number(access.days), _notes: access.notes.trim() || null });
    if (result.error) setMessage({ type: "error", text: `Não foi possível liberar o acesso: ${result.error.message}` });
    else {
      setMessage({ type: "success", text: access.type === "dorama" ? "Acesso ao título liberado." : "Acesso temporário liberado." });
      setAccess((current) => ({ ...current, email: "", notes: "" }));
      await loadTrials();
    }
    setBusy(false);
  }

  async function extendTrial(userId: string) {
    const { error } = await supabase.rpc("admin_extend_trial_access", { _user_id: userId, _extra_days: 7 });
    if (error) setMessage({ type: "error", text: error.message });
    else { setMessage({ type: "success", text: "Acesso prorrogado por 7 dias." }); await loadTrials(); }
  }

  async function revokeTrial(userId: string) {
    if (!window.confirm("Revogar este acesso temporário agora?")) return;
    const { error } = await supabase.rpc("admin_revoke_trial_access", { _user_id: userId });
    if (error) setMessage({ type: "error", text: error.message });
    else { setMessage({ type: "success", text: "Acesso revogado." }); await loadTrials(); }
  }

  return (
    <div className="page-container admin-page">
      <header className="admin-header">
        <div><p className="eyebrow">Área protegida</p><h1>Administração</h1><p>Gerencie o catálogo e os acessos sem sair do site.</p></div>
        <div className="admin-stats"><strong>{doramas.length}</strong><span>títulos cadastrados</span></div>
      </header>

      <div className="admin-tabs" role="tablist" aria-label="Seções administrativas">
        <button className={tab === "catalog" ? "active" : ""} onClick={() => setTab("catalog")}>Catálogo</button>
        <button className={tab === "access" ? "active" : ""} onClick={() => setTab("access")}>Acessos</button>
      </div>
      {message && <p className={`form-message ${message.type}`} role="status">{message.text}</p>}

      {tab === "catalog" ? (
        <div className="admin-catalog-layout">
          <form id="admin-editor" className="admin-panel admin-form" onSubmit={saveDorama}>
            <div className="admin-panel-heading"><div><span>{form.id ? "Editando" : "Novo título"}</span><h2>{form.id ? form.title : "Adicionar ao catálogo"}</h2></div>{form.id && <button type="button" className="text-button" onClick={() => setForm(emptyForm)}>Cancelar</button>}</div>
            <div className="admin-fields two-columns">
              <label>Título<input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
              <label>Título original<input value={form.original_title} onChange={(e) => setForm({ ...form, original_title: e.target.value })} /></label>
              <label>Slug<input placeholder="gerado automaticamente" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></label>
              <label>Categoria<select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{categories.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label>Formato<select value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })}><option value="complete">Completo</option><option value="episodes">Episódios</option><option value="both">Completo e episódios</option></select></label>
              <label>Preço<input required min="0" step="0.01" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></label>
              <label>Ano<input min="1900" max="2200" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></label>
              <label>Avaliação<input min="0" max="10" step="0.1" type="number" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></label>
              <label>Episódios<input min="0" type="number" value={form.episodes} onChange={(e) => setForm({ ...form, episodes: e.target.value })} /></label>
              <label>Grátis até<input type="datetime-local" value={form.free_until} onChange={(e) => setForm({ ...form, free_until: e.target.value })} /></label>
              <label>Elenco, separado por vírgulas<input value={form.cast_members} onChange={(e) => setForm({ ...form, cast_members: e.target.value })} /></label>
            </div>
            <label>Descrição<textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className="admin-fields two-columns">
              <label>URL da capa<input type="url" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} /></label>
              <label>URL da imagem principal<input type="url" value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} /></label>
              <label>URL do trailer<input type="url" value={form.trailer_url} onChange={(e) => setForm({ ...form, trailer_url: e.target.value })} /></label>
              <label>ID do trailer<input value={form.trailer_video_id} onChange={(e) => setForm({ ...form, trailer_video_id: e.target.value })} /></label>
            </div>
            <fieldset className="admin-media-fields"><legend>Mídia protegida</legend><p>Estes IDs só são consultados por contas com acesso.</p><div className="admin-fields two-columns">
              <label>ID principal<input value={form.full_video_id} onChange={(e) => setForm({ ...form, full_video_id: e.target.value })} /></label>
              <label>ID dublado<input value={form.full_video_id_dub} onChange={(e) => setForm({ ...form, full_video_id_dub: e.target.value })} /></label>
              <label>ID legendado<input value={form.full_video_id_sub} onChange={(e) => setForm({ ...form, full_video_id_sub: e.target.value })} /></label>
              <label>ID Bunny auxiliar<input value={form.bunny_video_id} onChange={(e) => setForm({ ...form, bunny_video_id: e.target.value })} /></label>
              <label>URL da pasta<input value={form.video_folder_url} onChange={(e) => setForm({ ...form, video_folder_url: e.target.value })} /></label>
            </div></fieldset>
            <div className="admin-checks"><label><input type="checkbox" checked={form.has_dubbing} onChange={(e) => setForm({ ...form, has_dubbing: e.target.checked })} /> Possui dublagem</label><label><input type="checkbox" checked={form.has_subtitles} onChange={(e) => setForm({ ...form, has_subtitles: e.target.checked })} /> Possui legendas</label><label><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publicado</label></div>
            <button className="primary-button" disabled={busy}>{busy ? "Salvando…" : form.id ? "Salvar alterações" : "Criar título"}</button>
          </form>

          <section className="admin-panel admin-list">
            <div className="admin-panel-heading"><div><span>Catálogo</span><h2>Títulos</h2></div></div>
            <label className="admin-search">Buscar<input type="search" placeholder="Título, categoria ou slug" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
            {catalogLoading ? <p>Carregando…</p> : <div className="admin-title-list">{filtered.map((item) => <article key={item.id}><div className="admin-thumb">{item.cover_url ? <img src={item.cover_url} alt="" /> : <span>Sem capa</span>}</div><div><strong>{item.title}</strong><span>{item.category} · R$ {Number(item.price ?? 0).toFixed(2).replace(".", ",")}</span><small>{item.published ? "Publicado" : "Rascunho"}{mediaByDorama[item.id] ? " · mídia pronta" : " · sem mídia"}</small></div><div className="admin-row-actions"><button onClick={() => { setForm(toForm(item, mediaByDorama[item.id])); document.getElementById("admin-editor")?.scrollIntoView({ behavior: "smooth" }); }}>Editar</button><button onClick={() => togglePublished(item)}>{item.published ? "Ocultar" : "Publicar"}</button></div></article>)}</div>}
          </section>
        </div>
      ) : (
        <div className="admin-access-layout">
          <form className="admin-panel admin-form" onSubmit={grantAccess}>
            <div className="admin-panel-heading"><div><span>Liberação manual</span><h2>Conceder acesso</h2></div></div>
            <label>Tipo de acesso<select value={access.type} onChange={(e) => setAccess({ ...access, type: e.target.value })}><option value="dorama">Um título</option><option value="trial">Passe temporário</option></select></label>
            <label>E-mail da conta<input required type="email" value={access.email} onChange={(e) => setAccess({ ...access, email: e.target.value })} /></label>
            {access.type === "dorama" ? <label>Título<select required value={access.doramaId} onChange={(e) => setAccess({ ...access, doramaId: e.target.value })}><option value="">Selecione</option>{doramas.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}</select></label> : <label>Dias de acesso<input required min="1" max="365" type="number" value={access.days} onChange={(e) => setAccess({ ...access, days: e.target.value })} /></label>}
            <label>Observação<textarea rows={3} value={access.notes} onChange={(e) => setAccess({ ...access, notes: e.target.value })} /></label>
            <button className="primary-button" disabled={busy}>{busy ? "Liberando…" : "Liberar acesso"}</button>
          </form>
          <section className="admin-panel admin-list">
            <div className="admin-panel-heading"><div><span>Acessos ativos</span><h2>Períodos de teste</h2></div></div>
            {trials.length === 0 ? <div className="admin-empty"><p>Nenhum acesso temporário ativo.</p></div> : <div className="trial-list">{trials.map((trial) => <article key={trial.user_id}><div><strong>{trial.display_name || trial.email}</strong><span>{trial.email}</span><small>Até {trial.expires_at ? new Date(trial.expires_at).toLocaleString("pt-BR") : "sem expiração"}</small></div><div className="admin-row-actions"><button onClick={() => extendTrial(trial.user_id)}>+7 dias</button><button className="danger" onClick={() => revokeTrial(trial.user_id)}>Revogar</button></div></article>)}</div>}
          </section>
        </div>
      )}
    </div>
  );
}
